import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Camera, Upload, Trash2, Loader2, Download, X, ChevronDown, ChevronUp, Image as ImageIcon, CheckSquare, Square,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

// 2026-05-27 (Phase 1A.1) — pick the URL that actually survives deploys.
// New uploads carry both `gridfs_id`+`persistent_url` (GridFS) and the
// legacy `file_url` (local disk). Disk gets wiped on production deploys
// so we prefer the GridFS URL when available; legacy rows still work.
const photoBestUrl = (photo) => {
  if (!photo) return "";
  if (photo.persistent_url) return resolveUrl(photo.persistent_url);
  if (photo.gridfs_id) return resolveUrl(`/api/files/national_photos/${photo.gridfs_id}`);
  return resolveUrl(photo.file_url);
};

/**
 * NationalPhotosPanel — list-first photo section for National orders.
 *   • Inspectors + Admin / Dispatch / Finance can delete a single photo
 *     or wipe the whole set before the order is finalised.
 *   • Photos render as rows (thumbnail + filename + uploader + date +
 *     per-row download/delete) so inspectors can review / correct fast.
 *   • This panel is mounted between Order Information and Additional Details.
 */
const NationalPhotosPanel = ({ orderId, userRole, title = "National Photos", toolbarLabel = "Upload Pictures of National", testIdPrefix = "national-photos", defaultOpen = true, showDownloadAll = true, uploadBatchSize = 20 }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [open, setOpen] = useState(defaultOpen);
  const [msg, setMsg] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  // Batch-download selection (Apr 21 2026 — user spec: NO ZIP).
  // Holds a Set of selected photo ids. Sequential normal-file downloads only.
  const [selected, setSelected] = useState(() => new Set());
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/national-photos`);
      setPhotos(res.data);
    } catch { setPhotos([]); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    // 2026-05-29 (Angela hard-lock fix) — defensive validation BEFORE we
    // try to upload anything. Three concrete problems we surface up-front:
    //   • iOS Safari sometimes returns File objects with size === 0 when
    //     the user picks many photos at once (well-known iOS bug). Those
    //     would upload successfully but appear as broken thumbnails.
    //   • Browsers may also emit empty entries when the user cancels.
    //   • Trust the size check, not just !file — File can be truthy yet
    //     unreadable.
    const skipped = [];
    const valid = [];
    for (const f of Array.from(files)) {
      if (!f) continue;
      if (typeof f.size === "number" && f.size === 0) {
        skipped.push(f.name || "(unnamed)");
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) {
      setMsg({
        type: "error",
        text: skipped.length
          ? `Skipped ${skipped.length} empty file${skipped.length === 1 ? "" : "s"}: ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}. Please re-select.`
          : "No valid photos selected.",
      });
      setTimeout(() => setMsg(null), 5000);
      return;
    }

    setUploading(true);
    setMsg(null);
    setUploadProgress({ current: 0, total: valid.length });

    // Batch upload (Apr 21 2026 — user spec): process files in sequential
    // batches of `uploadBatchSize` (default 20). 2026-05-29: dropped per-
    // batch concurrency from 4 → 2 — 4 parallel multipart POSTs were
    // saturating the backend (disk write + GridFS write + 2 Mongo updates
    // per request) on slow connections and producing silent timeouts.
    // 2 is the same level used by the working route-upload pattern.
    let success = 0;
    let done = 0;
    const failed = [];          // capture {name, reason} so we can surface them
    const CONCURRENCY = 2;
    const all = valid;
    const batchSize = Math.max(1, uploadBatchSize || 20);

    for (let start = 0; start < all.length; start += batchSize) {
      const batch = all.slice(start, start + batchSize);
      const queue = [...batch];
      const worker = async () => {
        while (queue.length) {
          const f = queue.shift();
          if (!f) break;
          try {
            const fd = new FormData();
            fd.append("file", f);
            // 2026-05-29 fix: explicit withCredentials + per-file timeout.
            // Drop the manual Content-Type header — letting axios+browser
            // build the multipart/form-data boundary automatically is the
            // safer pattern when concurrent uploads are in flight.
            await axios.post(
              `${API}/orders/${orderId}/national-photos`,
              fd,
              { withCredentials: true, timeout: 120000 },
            );
            success++;
          } catch (e) {
            const reason =
              e?.response?.status
                ? `HTTP ${e.response.status}`
                : (e?.code === "ECONNABORTED" ? "timeout" : (e?.message || "network error"));
            failed.push({ name: f.name || "(unnamed)", reason });
            console.error(`Failed to upload ${f.name}:`, e);
          }
          done++;
          setUploadProgress({ current: done, total: all.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batch.length) }, () => worker()));
    }

    await fetchPhotos();
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    // 2026-05-29 fix: surface EVERY failure (file name + reason) instead
    // of the previous silent console.error. Inspectors had no way of
    // knowing some photos didn't actually upload.
    const parts = [];
    parts.push(`${success} of ${all.length} photo${all.length === 1 ? "" : "s"} uploaded`);
    if (skipped.length) {
      parts.push(`skipped ${skipped.length} empty file${skipped.length === 1 ? "" : "s"}`);
    }
    if (failed.length) {
      const names = failed.slice(0, 3).map(x => `${x.name} (${x.reason})`).join(", ");
      parts.push(`failed: ${names}${failed.length > 3 ? ` +${failed.length - 3} more` : ""}`);
    }
    setMsg({
      type: (success === all.length && skipped.length === 0) ? "success" : "error",
      text: parts.join(" · "),
    });
    // Failures stay on screen longer so the user can read them.
    setTimeout(() => setMsg(null), failed.length || skipped.length ? 12000 : 5000);
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/orders/${orderId}/national-photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelected(prev => { const n = new Set(prev); n.delete(photoId); return n; });
      setMsg({ type: "success", text: "Photo deleted" });
      setTimeout(() => setMsg(null), 3000);
    } catch {
      setMsg({ type: "error", text: "Failed to delete photo" });
    }
  };

  // ═══ Selection + sequential batch download (NO ZIP) ═══════════════════
  // User spec: Select All / Clear / Download Selected must trigger NORMAL
  // file downloads — never a ZIP bundle. We fetch each selected photo as a
  // Blob from the same origin, then synthesise an <a download="…"> click
  // per file. Sequential with a tiny stagger so browsers that gate multi-
  // file downloads give a single "Allow" prompt and then pass them all.
  const toggleSelect = (photoId) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(photoId)) n.delete(photoId); else n.add(photoId);
      return n;
    });
  };

  const selectAll = () => {
    setSelected(new Set(photos.map(p => p.id)));
  };

  const clearSelection = () => {
    setSelected(new Set());
  };

  // 2026-05-26 (Angela hard-lock fix) — per-row download.
  // The previous `<a href download target="_blank">` only opened the image
  // in a new tab on iOS Safari and most desktop browsers because the file
  // is served from a different origin (REACT_APP_BACKEND_URL), which makes
  // the `download` attribute be ignored by the browser. We use the same
  // blob-fetch + programmatic anchor click that already powers
  // `downloadSelected` / `downloadAllZip` so it actually saves the file.
  const downloadOne = async (photo, index) => {
    if (!photo) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await axios.get(photoBestUrl(photo), { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.file_name || `photo-${(index ?? 0) + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(`Failed to download ${photo.file_name}:`, e);
      // iOS Safari still struggles with programmatic downloads when the
      // browser blocks the synthesised click. Fallback: open the file in a
      // new tab so the user can long-press → Save Image.
      try { window.open(photoBestUrl(photo), "_blank"); } catch (_) { /* ignore */ }
      setMsg({ type: "error", text: "Download blocked — opened in a new tab so you can save it manually" });
      setTimeout(() => setMsg(null), 4000);
    } finally {
      setBusy(false);
    }
  };

  const downloadSelected = async () => {
    const targets = photos.filter(p => selected.has(p.id));
    if (targets.length === 0) return;
    setBusy(true);
    setMsg(null);
    setDownloadProgress({ current: 0, total: targets.length });
    let ok = 0;
    for (let i = 0; i < targets.length; i++) {
      const p = targets[i];
      try {
        const res = await axios.get(photoBestUrl(p), { responseType: "blob" });
        const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = p.file_name || `photo-${i + 1}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Small stagger prevents some browsers from silently dropping
        // rapid-fire downloads in the same tick.
        await new Promise(r => setTimeout(r, 180));
        window.URL.revokeObjectURL(url);
        ok++;
      } catch (e) {
        console.error(`Failed to download ${p.file_name}:`, e);
      }
      setDownloadProgress({ current: i + 1, total: targets.length });
    }
    setBusy(false);
    setDownloadProgress({ current: 0, total: 0 });
    setMsg({
      type: ok === targets.length ? "success" : "error",
      text: `Downloaded ${ok} of ${targets.length} photo${targets.length === 1 ? "" : "s"}`,
    });
    setTimeout(() => setMsg(null), 4000);
  };
  // ══════════════════════════════════════════════════════════════════════

  const downloadAllZip = async () => {
    if (photos.length === 0) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/national-photos/zip`,
        {},
        { responseType: "blob", timeout: 120000 }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${orderId}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg({ type: "success", text: `Downloaded ${photos.length} photo${photos.length > 1 ? 's' : ''}` });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      console.error("ZIP download failed:", e);
      setMsg({ type: "error", text: "Failed to download ZIP" });
    } finally { setBusy(false); }
  };

  const handleDeleteAll = async () => {    if (photos.length === 0) return;
    setBusy(true);
    setMsg(null);
    let ok = 0;
    // Best-effort loop delete — most REST photo APIs don't expose a bulk
    // endpoint, so we iterate. Sequential keeps the UX honest (you can see
    // rows vanish). If any fails we still continue the rest.
    for (const p of photos) {
      try {
        await axios.delete(`${API}/orders/${orderId}/national-photos/${p.id}`);
        ok++;
      } catch (e) { /* ignore single-row fail */ }
    }
    await fetchPhotos();
    setBusy(false);
    setConfirmAll(false);
    setMsg({ type: ok === photos.length ? "success" : "error",
      text: `Deleted ${ok} / ${photos.length} photos` });
    setTimeout(() => setMsg(null), 4000);
  };

  // Inspector can now delete their own uploads too (user request Feb 2026 —
  // correct mistakes before finishing the order).
  const canDelete = ["Inspector", "Admin", "Dispatch", "Finance", "Scheduling"].includes(userRole);

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden" data-testid={`${testIdPrefix}-panel`}>
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/60 transition-colors"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-3">
          <Camera className="h-4 w-4 text-[#2F5D8A] shrink-0" />
          <span className="text-sm font-semibold text-[#1F2937]">{title}</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DCE4ED] text-[#2F5D8A]">
            {loading ? "..." : photos.length}
          </span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#E5E7EB] pt-4 space-y-3">
          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">
              {toolbarLabel}
            </p>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                className="hidden"
                onChange={e => { handleUpload(Array.from(e.target.files)); e.target.value = ""; }}
                data-testid="national-photo-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="px-3.5 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                data-testid="national-photo-upload-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading
                  ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                  : `Select Photos${uploadBatchSize ? ` (up to ${uploadBatchSize} / batch)` : ""}`
                }
              </button>
              {photos.length > 0 && showDownloadAll && (
                <button
                  onClick={downloadAllZip}
                  disabled={busy || uploading}
                  className="px-3.5 py-2 bg-white border border-[#2F5D8A] text-[#2F5D8A] hover:bg-[#2F5D8A]/8 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                  data-testid={`${testIdPrefix}-download-all-btn`}
                  title={`Download all ${photos.length} photo${photos.length > 1 ? 's' : ''} as a ZIP`}
                >
                  <Download className="h-3.5 w-3.5" /> Download All ({photos.length})
                </button>
              )}
              {photos.length > 0 && (
                <>
                  <button
                    onClick={selected.size === photos.length ? clearSelection : selectAll}
                    disabled={busy || uploading}
                    className="px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                    data-testid={`${testIdPrefix}-select-all-btn`}
                    title={selected.size === photos.length ? "Clear selection" : "Select all photos"}
                  >
                    {selected.size === photos.length
                      ? <><CheckSquare className="h-3.5 w-3.5" /> Clear Selection</>
                      : <><Square className="h-3.5 w-3.5" /> Select All</>
                    }
                  </button>
                  {selected.size > 0 && selected.size !== photos.length && (
                    <button
                      onClick={clearSelection}
                      disabled={busy || uploading}
                      className="px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                      data-testid={`${testIdPrefix}-clear-selection-btn`}
                    >
                      <X className="h-3.5 w-3.5" /> Clear ({selected.size})
                    </button>
                  )}
                  <button
                    onClick={downloadSelected}
                    disabled={busy || uploading || selected.size === 0}
                    className="px-3.5 py-2 bg-[#6B8F62] hover:bg-[#587752] text-white text-sm font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid={`${testIdPrefix}-download-selected-btn`}
                    title={selected.size === 0 ? "Select photos first" : `Download ${selected.size} selected photo${selected.size === 1 ? "" : "s"}`}
                  >
                    {busy && downloadProgress.total > 0
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Downloading {downloadProgress.current}/{downloadProgress.total}</>
                      : <><Download className="h-3.5 w-3.5" /> Download Selected{selected.size > 0 ? ` (${selected.size})` : ""}</>
                    }
                  </button>
                </>
              )}
              {canDelete && photos.length > 0 && (
                <button
                  onClick={() => setConfirmAll(true)}
                  disabled={busy || uploading}
                  className="px-3.5 py-2 bg-white border border-[#C07070] text-[#C07070] hover:bg-[#C07070]/8 text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                  data-testid="national-photo-delete-all-btn"
                  title="Delete every photo uploaded to this order"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete all
                </button>
              )}
            </div>
          </div>

          {/* Upload progress */}
          {uploading && uploadProgress.total > 0 && (
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2F5D8A] rounded-full transition-all duration-300"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          )}

          {/* Inline message */}
          {msg && (
            <div
              className={`px-3 py-2 rounded-lg text-xs font-medium ${msg.type === "success" ? "bg-[#EDF3EC] border border-[#C5D9C1] text-[#4A7A56]" : "bg-[#F3ECEC] border border-[#DFC4C4] text-[#8B4545]"}`}
              data-testid="national-photo-message"
            >
              {msg.text}
            </div>
          )}

          {/* List of uploaded photos */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : photos.length > 0 ? (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden" data-testid="national-photos-list">
              {photos.map((photo, i) => (
                <li
                  key={photo.id}
                  className={`flex items-center gap-3 px-3 py-2 transition-colors ${selected.has(photo.id) ? "bg-[#2F5D8A]/5 hover:bg-[#2F5D8A]/10" : "hover:bg-gray-50"}`}
                  data-testid={`national-photo-row-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(photo.id)}
                    onChange={() => toggleSelect(photo.id)}
                    className="h-4 w-4 accent-[#2F5D8A] cursor-pointer shrink-0"
                    data-testid={`${testIdPrefix}-row-checkbox-${i}`}
                    aria-label={`Select ${photo.file_name || `photo ${i + 1}`}`}
                  />
                  <button
                    type="button"
                    onClick={() => setLightbox(photo)}
                    className="flex-shrink-0 w-12 h-12 rounded border border-gray-200 overflow-hidden bg-gray-100"
                    title="Preview"
                  >
                    <img
                      src={photoBestUrl(photo)}
                      alt={photo.file_name || `Photo ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1F2937] font-medium truncate" title={photo.file_name}>
                      {photo.file_name || `Photo ${i + 1}`}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {photo.uploaded_by || "—"}
                      {photo.uploaded_at ? ` · ${new Date(photo.uploaded_at).toLocaleString()}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadOne(photo, i)}
                    disabled={busy}
                    className="p-2 text-gray-400 hover:text-[#2F5D8A] hover:bg-[#2F5D8A]/8 rounded transition-colors disabled:opacity-50"
                    title="Download"
                    data-testid={`national-photo-download-${i}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="p-2 text-gray-400 hover:text-[#C07070] hover:bg-[#C07070]/8 rounded transition-colors"
                      title="Delete this photo"
                      data-testid={`national-photo-delete-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 border border-dashed border-[#D1D5DB] rounded-lg bg-[#F9FAFB]" data-testid="national-photos-empty">
              <ImageIcon className="h-8 w-8 text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">No photos uploaded yet</p>
              <p className="text-gray-300 text-[10px] mt-0.5">Click "Upload Photos" to add inspection pictures</p>
            </div>
          )}

          {/* Delete-all confirmation */}
          {confirmAll && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4"
              onClick={() => !busy && setConfirmAll(false)}
              data-testid="national-photo-delete-all-modal"
            >
              <div
                className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-base font-bold text-[#1F2937] flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-[#C07070]" /> Delete all photos?
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  This will remove <b>{photos.length}</b> photo{photos.length === 1 ? "" : "s"} from this order. This cannot be undone.
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setConfirmAll(false)}
                    disabled={busy}
                    className="px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAll}
                    disabled={busy}
                    className="px-3 py-1.5 bg-[#C07070] hover:bg-[#A15252] text-white text-xs font-semibold rounded flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="national-photo-delete-all-confirm"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Yes, delete all
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lightbox */}
          {lightbox && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setLightbox(null)} data-testid="national-photo-lightbox">
              <div className="relative max-w-4xl max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <img
                  src={photoBestUrl(lightbox)}
                  alt={lightbox.file_name}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
                <button
                  onClick={() => setLightbox(null)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-4 py-2 rounded-b-lg">
                  <p className="text-white text-sm truncate">{lightbox.file_name}</p>
                  <p className="text-gray-300 text-[10px]">{lightbox.uploaded_by} &middot; {new Date(lightbox.uploaded_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NationalPhotosPanel;
